import React, { useEffect, useState, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import DataTable, { Column } from '../components/DataTable';
import JobStatusBadge from '../components/JobStatusBadge';
import { api } from '../services/api';
import type { AdminJob } from '../types';
import { fmtAge, fmtDuration } from '../utils/format';

export default function ErrorsPage() {
  const [jobs, setJobs] = useState<AdminJob[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/jobs?limit=500&status=failed')
      .then(r => setJobs(r.data.jobs))
      .finally(() => setLoading(false));
  }, []);

  const columns: Column<AdminJob>[] = [
    {
      key: 'type',
      label: 'Type',
      render: row => <span style={{ fontWeight: 600, color: 'var(--danger)' }}>{row.type}</span>,
    },
    {
      key: 'orgId',
      label: 'Org',
      render: row => (
        <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{row.orgId.slice(0, 12)}…</span>
      ),
    },
    {
      key: 'label',
      label: 'Job',
      render: row => <span style={{ fontWeight: 500 }}>{row.label}</span>,
    },
    {
      key: 'detail',
      label: 'Error Detail',
      render: row => (
        <span style={{ fontSize: 11, color: 'var(--text-muted)', wordBreak: 'break-word' }}>
          {row.detail ?? '—'}
        </span>
      ),
    },
    {
      key: 'result',
      label: 'Errors',
      render: row => (
        <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
          {row.result?.errors ?? '—'}
        </span>
      ),
    },
    {
      key: 'startedAt',
      label: 'When',
      sortable: true,
      render: row => fmtAge(row.startedAt),
    },
    {
      key: 'durationMs',
      label: 'Duration',
      render: row => fmtDuration(row.durationMs) || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: row => <JobStatusBadge status={row.status} />,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <AlertTriangle size={18} color="var(--danger)" />
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Error Log</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>
        Failed jobs from the last 24 hours. Use Accounts → Reprocess to fix zero-extraction meetings.
      </p>

      {!loading && jobs.length === 0 && (
        <div style={{
          padding: '32px',
          textAlign: 'center',
          color: 'var(--success)',
          fontSize: 14,
          background: 'var(--success-subtle)',
          border: '1px solid var(--success)',
          borderRadius: 8,
        }}>
          No failed jobs in the last 24 hours.
        </div>
      )}

      {(loading || jobs.length > 0) && (
        <DataTable<AdminJob>
          columns={columns}
          data={jobs}
          loading={loading}
          emptyMessage="No errors"
          rowKey={row => row.jobId}
        />
      )}
    </div>
  );
}
